import * as React from "react";
import {useState, useRef, useEffect} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faBullhorn, faEnvelopeOpenText, faListUl, faCopy, faCheck, faPencil, faRectangleWide, faXmark} from "@fortawesome/pro-regular-svg-icons";
import {IconDefinition} from "@fortawesome/fontawesome-svg-core";
import {useIntl} from "react-intl";
import IconAvatar from "./IconAvatar";
import StatusBadge from "./StatusBadge";

export type MarketingHeaderType = 'marketingEmail' | 'mailingList' | 'emailTemplate' | 'emailLayout';

type MarketingHeaderProps = {
    type: MarketingHeaderType;
    title: string;
    identifier: string;
    subtitle?: string;
    badge?: {
        value: string;
        label: string;
        states: {
            gray?: string[];
            green?: string[];
            yellow?: string[];
            red?: string[];
        };
    };
    onTitleEdit?: (newTitle: string) => void;
    isSaving?: boolean;
    actions?: React.ReactNode;
};

const typeIcons: Record<MarketingHeaderType, IconDefinition> = {
    marketingEmail: faBullhorn,
    mailingList: faListUl,
    emailTemplate: faEnvelopeOpenText,
    emailLayout: faRectangleWide,
};

const MarketingHeader: React.FC<MarketingHeaderProps> = ({
    type,
    title,
    identifier,
    subtitle,
    badge,
    onTitleEdit,
    isSaving = false,
    actions,
}) => {
    const intl = useIntl();
    const [copied, setCopied] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isTitleHovered, setIsTitleHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(title);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    useEffect(() => {
        setEditValue(title);
    }, [title]);

    const handleCopyIdentifier = async () => {
        try {
            await navigator.clipboard.writeText(identifier);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy identifier:', err);
        }
    };

    const handleEditClick = () => {
        if (onTitleEdit) {
            setIsEditing(true);
        }
    };

    const handleSave = () => {
        if (editValue.trim() && editValue !== title && onTitleEdit) {
            onTitleEdit(editValue.trim());
        }
        setIsEditing(false);
    };

    const handleCancel = () => {
        setEditValue(title);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const icon = typeIcons[type];

    return (
        <div className="mt-6 mb-6 mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between">
                <div className="flex items-center flex-1 min-w-0">
                    <IconAvatar icon={icon} size="md" />
                    <div className="ml-4 flex-1 min-w-0">
                    <div className="flex items-center">
                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <input
                                    ref={inputRef}
                                    type="text"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onKeyDown={handleKeyDown}
                                    className="text-2xl font-bold text-gray-900 border border-gray-300 rounded-md px-3 py-1 focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:border-red-500"
                                    style={{width: `${Math.max(editValue.length + 2, 12)}ch`}}
                                />
                                <button
                                    onClick={handleSave}
                                    className="p-2 text-green-600 hover:text-green-700 hover:bg-green-50 rounded-md transition-colors"
                                    title={intl.formatMessage({id: 'action.save'})}
                                >
                                    <FontAwesomeIcon icon={faCheck} className="text-lg" />
                                </button>
                                <button
                                    onClick={handleCancel}
                                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors"
                                    title={intl.formatMessage({id: 'button.cancel'})}
                                >
                                    <FontAwesomeIcon icon={faXmark} className="text-lg" />
                                </button>
                            </div>
                        ) : (
                            <h1
                                className="text-2xl font-bold text-gray-900 flex items-center group"
                                onMouseEnter={() => setIsTitleHovered(true)}
                                onMouseLeave={() => setIsTitleHovered(false)}
                            >
                                <span>{title}</span>
                                {onTitleEdit && (
                                    <button
                                        onClick={handleEditClick}
                                        disabled={isSaving}
                                        className={`ml-2 text-gray-400 hover:text-gray-600 transition-opacity duration-150 ${
                                            isTitleHovered ? 'opacity-100' : 'opacity-0'
                                        } ${isSaving ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                        title={intl.formatMessage({id: 'action.editTitle'})}
                                    >
                                        <FontAwesomeIcon icon={faPencil} className="text-base" />
                                    </button>
                                )}
                                {badge && (
                                    <StatusBadge
                                        value={badge.value}
                                        states={badge.states}
                                        label={badge.label}
                                        size="base"
                                        className="ml-4"
                                    />
                                )}
                            </h1>
                        )}
                    </div>
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleCopyIdentifier}
                            onMouseEnter={() => setIsHovered(true)}
                            onMouseLeave={() => setIsHovered(false)}
                            className={`flex items-center text-sm font-light transition-colors duration-150 ${
                                isHovered ? 'text-gray-700' : 'text-gray-400'
                            }`}
                            title={intl.formatMessage({id: 'action.copyIdentifier'})}
                        >
                            <span className="font-mono">{identifier}</span>
                            <FontAwesomeIcon
                                icon={copied ? faCheck : faCopy}
                                className={`ml-2 transition-opacity duration-150 ${
                                    isHovered || copied ? 'opacity-100' : 'opacity-0'
                                } ${copied ? 'text-green-600' : ''}`}
                            />
                        </button>
                        {subtitle && (
                            <>
                                <span className="text-gray-300">·</span>
                                <span className="text-sm text-gray-400">{subtitle}</span>
                            </>
                        )}
                    </div>
                    </div>
                </div>
                {actions && (
                    <div className="ml-4 shrink-0 flex items-center space-x-3">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
};

export default MarketingHeader;
